/** Langues de l'interface du reader. Le français reste la langue par défaut. */
export type Locale = "fr" | "en";

export const locales: readonly Locale[] = ["fr", "en"];

const LOCALE_KEY = "msb-reader:locale";

const isLocale = (value: unknown): value is Locale => value === "fr" || value === "en";

/**
 * Langue tirée des préférences du navigateur (navigator.languages, dans l'ordre) : la première
 * des deux qui apparaît l'emporte. Ni français ni anglais : français.
 */
export function detectLocale(languages: readonly string[]): Locale {
  for (const tag of languages) {
    const language = tag.trim().toLowerCase().split(/[-_]/)[0];
    if (isLocale(language)) return language;
  }
  return "fr";
}

/** Le choix explicite du lecteur passe avant le navigateur. */
export function resolveLocale(stored: string | null | undefined, languages: readonly string[]): Locale {
  return isLocale(stored) ? stored : detectLocale(languages);
}

export function loadLocale(): Locale | undefined {
  try {
    const stored = localStorage.getItem(LOCALE_KEY);
    return isLocale(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

export function saveLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_KEY, locale);
  } catch {
    // Stockage indisponible (navigation privée, quota) : le choix vaut pour cette visite.
  }
}

/** Langues préférées du navigateur, avec repli sur navigator.language. */
export function browserLanguages(): readonly string[] {
  if (typeof navigator === "undefined") return [];
  return navigator.languages?.length ? navigator.languages : navigator.language ? [navigator.language] : [];
}

/** Locale Intl pour les dates et les nombres. */
export const intlLocale = (locale: Locale) => (locale === "en" ? "en-GB" : "fr-FR");
